import React, {Component, ChangeEvent, KeyboardEvent, InputHTMLAttributes, ReactNode} from "react"
import {Col} from "react-bootstrap"

import ValidationInput, {ValidationState, ValidationProps} from "@irati/utils/validation/Input"
import id from "@irati/utils/id"
import {InjectIntl, injectIntl, getResolvedMessage} from "@irati/utils/lang"
import {FormGroup} from "@irati/ui/src/Form"
import ErrorText from "@irati/ui/src/ErrorText"
import onEnter from "@irati/utils/onEnter"

import {StyledCol, StyledLabel, CheckboxUI, CheckboxInput, CheckboxOutline} from "./styled"

export interface Props extends InjectIntl, Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">, ValidationProps<boolean> {
  id?: string,
  className?: string,
  checked?: boolean,
  disabled?: boolean,
  flush?: boolean,
  children: ReactNode,
  name: string,
  onChange: (event: CheckboxEvent) => void
}

type CheckboxEvent = ChangeEvent<HTMLInputElement> | KeyboardEvent<HTMLLabelElement>

interface State extends ValidationState<boolean> {}

class Checkbox extends Component<Props, State> {
  validation = new ValidationInput(this)
  state: State = {
    errorMsg: undefined
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (nextProps.checked !== prevState.value) {
      return {value: nextProps.checked}
    }
    return null
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => this.validation.update(prevProps, prevState)
  componentWillUnmount = () => this.validation.destroy()
  handleChange = (event: CheckboxEvent) => this.props.onChange(event)

  render() {
    const {errorMsg, value} = this.state
    const {className, name, disabled, children, flush, tabIndex} = this.props

    const idNamespace = id("checkbox", this.props)
    const idCheckbox = idNamespace || name
    const validationState = errorMsg ? "error" : undefined

    return (
      <FormGroup flush={flush} controlId={`${idCheckbox}`} validationState={validationState}>
        <Col xs={12}>
          <CheckboxUI className={className}>
            <CheckboxInput
              id={idCheckbox}
              type="checkbox"
              checked={value!}
              disabled={disabled}
              onChange={this.handleChange}
              tabIndex={-1} />
            <CheckboxOutline
              htmlFor={idCheckbox}
              disabled={disabled}
              tabIndex={tabIndex}
              onKeyDown={(e) => onEnter(e, () => this.handleChange(e))} />
          </CheckboxUI>
          <StyledLabel id={id("label", idNamespace)} htmlFor={idCheckbox}>
            {children}
          </StyledLabel>
        </Col>
        {errorMsg && (
          <StyledCol xs={12}>
            <ErrorText small>{getResolvedMessage(this, "errorMsg", this.state)}</ErrorText>
          </StyledCol>
        )}
      </FormGroup>
    )
  }
}

export default injectIntl(Checkbox)
